import type { AflsStepConfig } from "./types";

/**
 * AFLS (Assessment of Functional Living Skills) step configuration.
 * 6 modules each with representative skill items and score ranges.
 * Skills with `isTotal: true` are auto-calculated in the UI and excluded
 * from the initial form state; their values must not be sent directly to
 * the backend.
 * Non-total skills carry an `apiField` (camelCase) that maps to the
 * snake_case key stored in the backend after normalization.
 */
export const AFLS_STEP_CONFIGS: AflsStepConfig[] = [
  {
    id: 1,
    title: "Basic skills",
    question: "Include Basic skills scoring in the assessment",
    scoreRange: "0.0 - 642.0",
    domainTotalField: "basicSkillsTotal",
    skills: [
      { id: "bs-total", name: "Basic skills total", maxScore: 642, isTotal: true },
      { id: "bs-1", name: "Self management", maxScore: 58, apiField: "selfManagement" },
      { id: "bs-2", name: "Basic communication", maxScore: 78, apiField: "basicCommunication" },
      { id: "bs-3", name: "Dressing", maxScore: 120, apiField: "dressing" },
      { id: "bs-4", name: "Toileting", maxScore: 98, apiField: "toileting" },
      { id: "bs-5", name: "Grooming", maxScore: 106, apiField: "grooming" },
      { id: "bs-6", name: "Bathing", maxScore: 38, apiField: "bathing" },
      {
        id: "bs-7",
        name: "Health, safety, and first aid",
        maxScore: 110,
        apiField: "healthSafetyFirstAid",
      },
      { id: "bs-8", name: "Nighttime routines", maxScore: 34, apiField: "nighttimeRoutines" },
    ],
  },
  {
    id: 2,
    title: "Home skills",
    question: "Include Home skills scoring in the assessment",
    scoreRange: "0.0 - 678.0",
    domainTotalField: "homeSkillsTotal",
    skills: [
      { id: "hs-total", name: "Home skills total", maxScore: 678, isTotal: true },
      { id: "hs-1", name: "Meals at home", maxScore: 58, apiField: "mealsAtHome" },
      { id: "hs-2", name: "Dishes", maxScore: 52, apiField: "dishes" },
      { id: "hs-3", name: "Clothing & laundry", maxScore: 68, apiField: "clothingAndLaundry" },
      {
        id: "hs-4",
        name: "Housekeeping & chores",
        maxScore: 82,
        apiField: "housekeepingAndChores",
      },
      { id: "hs-5", name: "Household mechanics", maxScore: 88, apiField: "householdMechanics" },
      { id: "hs-6", name: "Leisure", maxScore: 72, apiField: "leisure" },
      { id: "hs-7", name: "Kitchen", maxScore: 120, apiField: "kitchen" },
      { id: "hs-8", name: "Cooking", maxScore: 138, apiField: "cooking" },
    ],
  },
  {
    id: 3,
    title: "School skills",
    question: "Include School skills scoring in the assessment",
    scoreRange: "0.0 - 956.0",
    domainTotalField: "schoolSkillsTotal",
    skills: [
      { id: "ss-total", name: "School skills total", maxScore: 956, isTotal: true },
      { id: "ss-1", name: "Classroom mechanics", maxScore: 44, apiField: "classroomMechanics" },
      { id: "ss-2", name: "Meals at school", maxScore: 78, apiField: "mealsAtSchool" },
      {
        id: "ss-3",
        name: "Routines and expectations",
        maxScore: 136,
        apiField: "routinesAndExpectations",
      },
      { id: "ss-4", name: "Social skills", maxScore: 88, apiField: "schoolSocialSkills" },
      { id: "ss-5", name: "Technology", maxScore: 128, apiField: "technology" },
      { id: "ss-6", name: "Common knowledge", maxScore: 142, apiField: "commonKnowledge" },
      { id: "ss-7", name: "Core academics", maxScore: 174, apiField: "coreAcademics" },
      { id: "ss-8", name: "Applied academics", maxScore: 166, apiField: "appliedAcademics" },
    ],
  },
  {
    id: 4,
    title: "Community skills",
    question: "Include Community skills scoring in the assessment",
    scoreRange: "0.0 - 670.0",
    domainTotalField: "communitySkillsTotal",
    skills: [
      { id: "cs-total", name: "Community skills total", maxScore: 670, isTotal: true },
      { id: "cs-1", name: "Basic mobility", maxScore: 110, apiField: "basicMobility" },
      { id: "cs-2", name: "Community knowledge", maxScore: 82, apiField: "communityKnowledge" },
      { id: "cs-3", name: "Shopping", maxScore: 114, apiField: "shopping" },
      { id: "cs-4", name: "Eating in public", maxScore: 64, apiField: "eatingInPublic" },
      { id: "cs-5", name: "Money", maxScore: 54, apiField: "money" },
      { id: "cs-6", name: "Phone", maxScore: 74, apiField: "phone" },
      { id: "cs-7", name: "Time", maxScore: 64, apiField: "time" },
      {
        id: "cs-8",
        name: "Social awareness and manners",
        maxScore: 108,
        apiField: "socialAwarenessAndManners",
      },
    ],
  },
  {
    id: 5,
    title: "Independent living skills",
    question: "Include Independent living scoring in the assessment",
    scoreRange: "0.0 - 1123.0",
    domainTotalField: "independentLivingTotal",
    skills: [
      { id: "il-total", name: "Independent living total", maxScore: 1123, isTotal: true },
      { id: "il-1", name: "Organizational skills", maxScore: 62, apiField: "organizationalSkills" },
      { id: "il-2", name: "Self care", maxScore: 95, apiField: "selfCare" },
      {
        id: "il-3",
        name: "Maintenance and cleaning",
        maxScore: 94,
        apiField: "maintenanceAndCleaning",
      },
      { id: "il-4", name: "Mechanics and repair", maxScore: 62, apiField: "mechanicsAndRepair" },
      { id: "il-5", name: "Community travel", maxScore: 74, apiField: "communityTravel" },
      { id: "il-6", name: "Transportation", maxScore: 88, apiField: "transportation" },
      {
        id: "il-7",
        name: "Kitchen tools and appliances",
        maxScore: 70,
        apiField: "kitchenToolsAndAppliances",
      },
      { id: "il-8", name: "Food and meal planning", maxScore: 60, apiField: "foodAndMealPlanning" },
      { id: "il-9", name: "Money management", maxScore: 86, apiField: "moneyManagement" },
      { id: "il-10", name: "Independent shopping", maxScore: 50, apiField: "independentShopping" },
      { id: "il-11", name: "Personal management", maxScore: 72, apiField: "personalManagement" },
      { id: "il-12", name: "Safety awareness", maxScore: 74, apiField: "safety" },
      { id: "il-13", name: "Problem solving", maxScore: 54, apiField: "problemSolving" },
      { id: "il-14", name: "Social interactions", maxScore: 68, apiField: "socialInteractions" },
      { id: "il-15", name: "Living with others", maxScore: 38, apiField: "livingWithOthers" },
      {
        id: "il-16",
        name: "Interpersonal relationships",
        maxScore: 76,
        apiField: "interpersonalRelationships",
      },
    ],
  },
  {
    id: 6,
    title: "Vocational skills",
    question: "Include Vocational skills scoring in the assessment",
    scoreRange: "0.0 - 1216.0",
    domainTotalField: "vocationalSkillsTotal",
    skills: [
      { id: "vs-total", name: "Vocational skills total", maxScore: 1216, isTotal: true },
      { id: "vs-1", name: "Job search", maxScore: 68, apiField: "jobSearch" },
      { id: "vs-2", name: "Interview", maxScore: 58, apiField: "interview" },
      { id: "vs-3", name: "Basic skills", maxScore: 98, apiField: "vocationalBasicSkills" },
      { id: "vs-4", name: "Coworker relations", maxScore: 38, apiField: "coworkerRelations" },
      { id: "vs-5", name: "Workplace safety", maxScore: 80, apiField: "workplaceSafety" },
      { id: "vs-6", name: "Fixed activity skills", maxScore: 26, apiField: "fixedActivitySkills" },
      { id: "vs-7", name: "Custodial & cleaning", maxScore: 114, apiField: "custodialAndCleaning" },
      { id: "vs-8", name: "Laundry", maxScore: 32, apiField: "laundry" },
      { id: "vs-9", name: "Retail", maxScore: 50, apiField: "retail" },
      { id: "vs-10", name: "Support personnel", maxScore: 12, apiField: "supportPersonnel" },
      { id: "vs-11", name: "Office skills", maxScore: 116, apiField: "officeSkills" },
      { id: "vs-12", name: "Computer skills", maxScore: 102, apiField: "computerSkills" },
      { id: "vs-13", name: "Restaurant skills", maxScore: 90, apiField: "restaurantSkills" },
      { id: "vs-14", name: "Restaurant kitchen", maxScore: 68, apiField: "restaurantKitchen" },
      { id: "vs-15", name: "Warehouse", maxScore: 34, apiField: "warehouse" },
      { id: "vs-16", name: "Tools", maxScore: 80, apiField: "tools" },
      {
        id: "vs-17",
        name: "Trades & construction",
        maxScore: 100,
        apiField: "tradesAndConstruction",
      },
      { id: "vs-18", name: "Landscaping", maxScore: 50, apiField: "landscaping" },
    ],
  },
];

/**
 * Build initial AFLS form step states from step configs, with null scores.
 * Total-flagged skills are excluded from the editable state; they are
 * auto-calculated in the UI from the sub-skill scores.
 */
export function buildInitialAflsSteps() {
  return AFLS_STEP_CONFIGS.map((config) => ({
    stepId: config.id,
    stepCompleted: true,
    skills: config.skills
      .filter((skill) => !skill.isTotal)
      .map((skill) => ({ ...skill, score: null })),
  }));
}
